import {injectable} from 'inversify';
import {makeAutoObservable, reaction} from 'mobx';

import type {CardApiModel} from '../../../api/payment';
import type {Destructible, WithReactionDispose} from '../../../utils/store';

import {cardStore} from '../../../api/payment';
import {createCollectionStore} from '../../../api/utils/collectionStore';
import {alert} from '../../../components/alert';
import {cardDelete} from '../../../services/entity-action/card/card.delete';
import {isCancelCommand} from '../../../services/entity-action/utils';
import {ReactionDisposeManager} from '../../../utils/store';

@injectable()
export class PaymentMethodStore implements Destructible, WithReactionDispose {
  disposer = new ReactionDisposeManager();
  private readonly cardsStore = createCollectionStore<CardApiModel>({
    method: 'user.cards.list',
    params: {
      query: [['type', '==', 'card']],
      sort: [['created', -1]],
    },
  });
  private selectedCardId: null | string = null;

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.init();

    this.setupReactions();
  }

  private init() {
    this.fetch();
  }

  destruct() {
    this.cardsStore.clear();
  }

  get cards(): CardApiModel[] {
    return this.cardsStore.items;
  }

  get cardsById(): Map<string, CardApiModel> {
    const cardsById = new Map<string, CardApiModel>();

    this.cardsStore.items.forEach(card => cardsById.set(card._id, card));

    return cardsById;
  }

  get hasCards(): boolean {
    return this.cardsStore.hasItems;
  }

  get isFetching(): boolean {
    return this.cardsStore.isFetching;
  }

  get selectedCard(): CardApiModel | undefined {
    return this.selectedCardId
      ? this.cardsById.get(this.selectedCardId)
      : undefined;
  }

  async deleteCard(card: CardApiModel) {
    try {
      const result = await cardDelete({entity: card});

      if (isCancelCommand(result)) {
        return;
      }

      this.cardsStore.removeItem(card);
    } catch (error) {
      alert.error(error);
    }
  }

  deleteLocalCards(cards: CardApiModel[]) {
    cards.forEach(card => this.cardsStore.removeItem(card));
  }

  fetch() {
    this.cardsStore.fetch();
  }

  isCardDeletable(card: CardApiModel): boolean {
    return !this.isCardSelected(card);
  }

  isCardSelected(card: CardApiModel): boolean {
    return card._id === this.selectedCardId;
  }

  setSelectedCardId(cardId: null | string) {
    this.selectedCardId = cardId;
  }

  private setupReactions() {
    this.disposer.watch(
      'cardStore.creating',
      reaction(
        () => cardStore.creating,
        creating => {
          if (creating.success && creating.entity) {
            this.cardsStore.addItem(creating.entity);
          }
        },
      ),
    );
  }
}
